import { DatePicker } from 'antd'
import styled from 'styled-components'
import { Button } from 'sutra/components/Button'

import {
  buttonBlue,
  cardBorder,
  darkBlueFontColor,
} from 'assets/styles/variables'

// Shared styled primitives for agent-config forms (issue + benchmarking).
// Consumed by both the customer-facing wrappers (wizard step, edit panel)
// in `features/trend-detection/` and the Yogi-staff admin modals in
// `features/settings/features/agents/`. Lives in `src/components/` because
// neither feature owns these primitives anymore — they're the team's
// vocabulary for agent-config UIs.

// ─── Form layout primitives ──────────────────────────────────────────────────

export const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const LabelGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

export const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: ${darkBlueFontColor};
`

export const HelperText = styled.p`
  font-size: 12px;
  color: ${darkBlueFontColor};
  opacity: 0.6;
  margin: 0;
`

export const StyledInput = styled.input`
  font-size: 13px;
  padding: 8px 12px;
  border: 2px solid #e5e7eb;
  border-radius: 6px;
  width: 100%;
  transition: all 0.3s;
  background-color: white;

  &::placeholder {
    color: #999;
    font-style: italic;
  }

  &:focus {
    outline: none;
    border: 2px solid #3b82f6;
    background-color: white;
  }

  &:hover:not(:focus) {
    border-color: #d1d5db;
  }
`

// ─── Segmented option group (sensitivity / entity-type chips) ───────────────

export const OptionGroup = styled.div`
  display: flex;
  gap: 0;
  border: 1px solid ${cardBorder};
  border-radius: 6px;
  overflow: hidden;
  width: fit-content;
`

export const OptionButton = styled.div<{ $active: boolean }>`
  padding: 5px 14px;
  font-size: 12px;
  font-weight: 400;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  transition:
    background-color 0.15s,
    color 0.15s;
  background-color: ${({ $active }) =>
    $active ? 'rgba(80, 114, 164, 0.1)' : 'white'};
  color: ${({ $active }) => ($active ? buttonBlue : darkBlueFontColor)};
  border-right: 1px solid ${cardBorder};

  &:last-child {
    border-right: none;
  }

  &:hover {
    ${({ $active }) => !$active && 'background-color: #f0f0f0;'}
  }
`

// ─── Filter picker primitives ────────────────────────────────────────────────

export const FilterActionsRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  width: 100%;
`

export const FilterActionsSlot = styled.div`
  flex: 1;
  min-width: 0;
`

export const AddFilterBlock = styled.div`
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 12px 10px 10px;
`

export const SpinWrapper = styled.div`
  text-align: center;
  padding: 20px;
`

export const FullWidthRangePicker = styled(DatePicker.RangePicker)`
  width: 100%;
`

export const FilterSearchInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 4px 8px 4px 16px;
  margin-bottom: 4px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 12px;
  outline: none;
  height: 28px;
  box-sizing: border-box;
  color: rgba(0, 0, 0, 0.85);
  &::placeholder {
    color: rgba(0, 0, 0, 0.55);
  }
  &:focus {
    border-color: #40a9ff;
  }
`

export const ClearButton = styled(Button)`
  flex-shrink: 0;
  margin-left: auto;
`
